// src/pages/AdminDashboardPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AdminIAAlertsPage from "./AdminIAAlertsPage";
import AdminQuotesTable from "./AdminQuotesTable";
import ArtistQuoteForm from "../components/ArtistQuoteForm";
import CotationAssistPanel from "../components/CotationAssistPanel";
import AdminUserRoles from "../components/AdminUserRoles";
import IAAlertForm from "../components/IAAlertForm";
import PendingProfilesList from "../components/PendingProfilesList";
import { supabase } from "../lib/supabaseClient";
import EstimationComparableForm from "../components/EstimationComparableForm";
import AccessLogsTable from "../components/matching-intergenerationnel/AccessLogsTable";
import FicheForm from "../components/FicheForm";
import FichesList from "../components/FichesList";
import AdminGlobalPartners from "./AdminGlobalPartners";
import ImportArtprice from "./expert/ImportArtprice";
import AdminSubscriptions from "./AdminSubscriptions";
import AdminSubscriptionEvents from "./AdminSubscriptionEvents";
import AdminSubscriptionsWithEvents from "./AdminSubscriptionsWithEvents";
import AdminExpertsManager from "../components/AdminExpertsManager";
import AdminValidationRequests from "../components/AdminValidationRequests";
import AdminBilling from "../components/admin/AdminBilling";
import AdminFeatureToggles from "../components/admin/AdminFeatureToggles";
import ImportDataAdmin from "../components/ImportDataAdmin";
import SharedDashboard from "../components/SharedDashboard";
import AdminChangeSubscription from "../components/admin/AdminChangeSubscription";
import AdminServiceIncidents from "../components/admin/AdminServiceIncidents";
import {
  UserCheck,
  CheckCircle2,
  Award,
  Share2,
  ShieldCheck,
  ScrollText,
  CreditCard,
  RefreshCcw,
  Webhook,
  Receipt,
  RotateCcw,
  Upload,
  TrendingUp,
  PlusCircle,
  Gavel,
  FilePlus2,
  BookOpen,
  Sparkles,
  BellPlus,
  SlidersHorizontal,
  Globe2,
  AlertTriangle,
  Mail,
  Gift,
  IdCard,
} from "lucide-react";

const EMPTY_METRICS = {
  pendingProfiles: 0,
  newProfiles7d: 0,
  newProfilesCampaign7d: 0,
  activeSubscriptions: 0,
  paymentIncidents: 0,
  artworks: 0,
  aiEnabledProfiles: 0,
  trialsActive: 0,
  trialsExpiring3d: 0,
  serviceIncidents: 0,
};

const SECTIONS = [
  {
    id: "users",
    title: "Utilisateurs & accès",
    description: "Validation, rôles, experts et traçabilité",
    accent: "blue",
    items: [
      { id: "pending", label: "Comptes à approuver", hint: "Nouveaux profils", icon: UserCheck, badgeKey: "pendingProfiles" },
      { id: "validation-requests", label: "Validations métier", hint: "Estimations à contrôler", icon: CheckCircle2 },
      { id: "experts", label: "Experts", hint: "Accès et rattachements", icon: Award },
      { id: "global-partners", label: "Partenaires & accès", hint: "Comptes, conventions et conditions", icon: Globe2 },
      { id: "shared", label: "Accès partagés", hint: "Experts et équipes", icon: Share2 },
      { id: "roles", label: "Rôles & permissions", hint: "Administration des profils", icon: ShieldCheck },
      { id: "heir-access-logs", label: "Logs héritiers", hint: "Consultations tracées", icon: ScrollText },
    ],
  },
  {
    id: "billing",
    title: "Abonnements & revenus",
    description: "Plans, webhooks, facturation et incidents",
    accent: "green",
    items: [
      { id: "subscriptions", label: "Abonnements", hint: "Vue complète", icon: CreditCard },
      { id: "subscriptions-with-events", label: "Vue réconciliée", hint: "Abonnements + événements", icon: RefreshCcw },
      { id: "subscription-events", label: "Événements Stripe", hint: "Webhooks reçus", icon: Webhook },
      { id: "billing", label: "Facturation pro", hint: "Factures et versements", icon: Receipt },
      { id: "change-subscription", label: "Changer un plan", hint: "Correction administrateur", icon: RotateCcw },
    ],
  },
  {
    id: "data",
    title: "Données & valorisation",
    description: "Imports, cotes, comparables et ressources",
    accent: "rose",
    items: [
      { id: "import-data", label: "Importer une collection", hint: "CSV / XLSX vers œuvres", icon: Upload },
      { id: "quotes", label: "Cotes artistes", hint: "Base de référence", icon: TrendingUp },
      { id: "form", label: "Ajouter une cote", hint: "Nouvelle référence", icon: PlusCircle },
      { id: "comparable-form", label: "Ajouter un comparable", hint: "Vente réalisée", icon: Gavel },
      { id: "fiches", label: "Créer une fiche", hint: "Ressource pratique", icon: FilePlus2 },
      { id: "voir-fiches", label: "Bibliothèque de fiches", hint: "Consulter les ressources", icon: BookOpen },
    ],
  },
  {
    id: "ai",
    title: "IA & exploitation",
    description: "Alertes, contenus et paramètres de plateforme",
    accent: "purple",
    items: [
      { id: "alerts", label: "Alertes IA", hint: "Signaux à consulter", icon: Sparkles },
      { id: "add-alert", label: "Créer une alerte", hint: "Alerte éditoriale", icon: BellPlus },
      { id: "feature-toggles", label: "Fonctionnalités", hint: "Activer ou suspendre", icon: SlidersHorizontal },
      { id: "service-incidents", label: "Incidents service", hint: "Erreurs techniques captées", icon: AlertTriangle, badgeKey: "serviceIncidents" },
    ],
  },
];

const ACCENTS = {
  blue: {
    bar: "bg-blue-600",
    soft: "bg-blue-50 border-blue-100",
    active: "bg-blue-600 text-white border-blue-600 shadow-sm",
    icon: "bg-blue-100 text-blue-700",
  },
  green: {
    bar: "bg-emerald-600",
    soft: "bg-emerald-50 border-emerald-100",
    active: "bg-emerald-600 text-white border-emerald-600 shadow-sm",
    icon: "bg-emerald-100 text-emerald-700",
  },
  rose: {
    bar: "bg-rose-500",
    soft: "bg-rose-50 border-rose-100",
    active: "bg-rose-500 text-white border-rose-500 shadow-sm",
    icon: "bg-rose-100 text-rose-700",
  },
  purple: {
    bar: "bg-violet-600",
    soft: "bg-violet-50 border-violet-100",
    active: "bg-violet-600 text-white border-violet-600 shadow-sm",
    icon: "bg-violet-100 text-violet-700",
  },
};

function MetricCard({ label, value, helper, tone = "slate", onClick }) {
  const toneClasses = {
    slate: "bg-white border-gray-200",
    blue: "bg-blue-50 border-blue-100",
    green: "bg-emerald-50 border-emerald-100",
    amber: "bg-amber-50 border-amber-100",
    rose: "bg-rose-50 border-rose-100",
    purple: "bg-violet-50 border-violet-100",
  };

  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-2xl border p-5 text-left ${toneClasses[tone]} ${onClick ? "transition hover:-translate-y-0.5 hover:shadow-md" : ""}`}
    >
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-gray-950">{value}</div>
      <div className="mt-1 text-sm text-gray-500">{helper}</div>
    </Component>
  );
}

const CORNER_BTN =
  "rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 transition hover:border-gray-300 hover:bg-white hover:shadow-sm";

function CornerKpi({ label, value, tone = "slate" }) {
  const tones = {
    slate: "text-gray-900",
    green: "text-emerald-700",
    rose: "text-rose-700",
    amber: "text-amber-700",
    violet: "text-violet-700",
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function NavSection({ section, activeTab, metrics, onSelect }) {
  const colors = ACCENTS[section.accent];
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className={`h-1.5 ${colors.bar}`} />
      <div className="p-5">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-950">{section.title}</h3>
          <p className="mt-1 text-sm text-gray-500">{section.description}</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {section.items.map((item) => {
            const isActive = activeTab === item.id;
            const badge = item.badgeKey ? metrics[item.badgeKey] : null;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item.id)}
                className={`relative flex min-h-[78px] items-start gap-3 rounded-xl border p-3 text-left transition ${
                  isActive
                    ? colors.active
                    : "border-gray-200 bg-gray-50 text-gray-800 hover:border-gray-300 hover:bg-white hover:shadow-sm"
                }`}
              >
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? "bg-white/15 text-white" : colors.icon}`}>
                  {Icon && <Icon className="h-4 w-4" strokeWidth={1.75} />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{item.label}</span>
                  <span className={`mt-1 block text-xs ${isActive ? "text-white/75" : "text-gray-500"}`}>{item.hint}</span>
                </span>
                {badge > 0 && (
                  <span className="absolute right-2 top-2 rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [quotePrefill, setQuotePrefill] = useState(null);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [health, setHealth] = useState({ status: "checking", label: "Vérification…" });
  const [lastRefresh, setLastRefresh] = useState(null);
  const contentRef = useRef(null);

  const allItems = useMemo(() => SECTIONS.flatMap((section) => section.items), []);
  const activeItem = allItems.find((item) => item.id === activeTab);

  // Sonde Stripe (webhook joignable + secrets posés). Renseignée en parallèle.
  const [stripeHealth, setStripeHealth] = useState(null);
  // Cockpits par métier (coins CFO / CMO).
  const [cmo, setCmo] = useState(null);
  const [cfo, setCfo] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoadingMetrics(true);
    setHealth({ status: "checking", label: "Vérification…" });

    try {
      // Une seule RPC admin-gated (remplace 6 count() client, indépendant des RLS).
      const { data, error } = await supabase.rpc("admin_dashboard_metrics");
      if (error) throw error;

      // Incidents service non résolus (table à part, lecture admin via RLS).
      let serviceIncidents = 0;
      try {
        const { count } = await supabase
          .from("service_incidents")
          .select("id", { count: "exact", head: true })
          .eq("resolved", false);
        serviceIncidents = count || 0;
      } catch {
        serviceIncidents = 0;
      }

      setMetrics({ ...EMPTY_METRICS, ...(data || {}), serviceIncidents });
      setHealth({ status: "online", label: "Supabase opérationnel" });
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Admin dashboard metrics error:", error);
      setHealth({ status: "degraded", label: "Données indisponibles" });
    } finally {
      setLoadingMetrics(false);
    }

    // Sonde Stripe (non bloquante : n'affecte ni les métriques ni la santé Supabase).
    try {
      const { data: sh } = await supabase.functions.invoke("admin-stripe-health", { body: {} });
      setStripeHealth(sh || null);
    } catch {
      setStripeHealth(null);
    }

    // Coin CMO : entonnoir CRM (RLS admin/team).
    try {
      const { data: leads } = await supabase.from("crm_leads").select("stage");
      if (leads) {
        const ORDER = ["lead", "qualifie", "demo", "essai", "client"];
        const active = leads.filter((l) => l.stage !== "perdu").length;
        const reachedClient = leads.filter((l) => ORDER.indexOf(l.stage) >= ORDER.indexOf("client")).length;
        setCmo({
          total: leads.length,
          client: leads.filter((l) => l.stage === "client").length,
          conv: active > 0 ? Math.round((reachedClient / active) * 100) : 0,
        });
      }
    } catch {
      setCmo(null);
    }

    // Coin CFO : factures + conformité e-facture (RLS admin/team).
    try {
      const { data: inv } = await supabase
        .from("invoices")
        .select("id, pennylane_invoice_id, buyer_is_business")
        .limit(5000);
      if (inv) {
        setCfo({
          count: inv.length,
          synced: inv.filter((i) => i.pennylane_invoice_id).length,
          b2b: inv.filter((i) => i.buyer_is_business).length,
        });
      }
    } catch {
      setCfo(null);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== "overview") {
      requestAnimationFrame(() => {
        contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderActiveTool = () => {
    switch (activeTab) {
      case "import-data": return <ImportDataAdmin />;
      case "quotes": return (
        <>
          <CotationAssistPanel
            onPrefill={(v) => { setQuotePrefill(v); handleTabChange("form"); }}
          />
          <AdminQuotesTable />
        </>
      );
      case "alerts": return <AdminIAAlertsPage />;
      case "validation-requests": return <AdminValidationRequests />;
      case "form": return <ArtistQuoteForm prefill={quotePrefill} />;
      case "global-partners": return <AdminGlobalPartners />;
      case "roles": return <AdminUserRoles />;
      case "add-alert": return <IAAlertForm />;
      case "pending": return <PendingProfilesList />;
      case "comparable-form":
        return (
          <EstimationComparableForm
            onCancel={() => handleTabChange("quotes")}
            onSubmit={() => handleTabChange("quotes")}
          />
        );
      case "heir-access-logs": return <AccessLogsTable />;
      case "fiches": return <FicheForm />;
      case "voir-fiches": return <FichesList />;
      case "subscriptions": return <AdminSubscriptions />;
      case "change-subscription": return <AdminChangeSubscription />;
      case "subscription-events": return <AdminSubscriptionEvents />;
      case "subscriptions-with-events": return <AdminSubscriptionsWithEvents />;
      case "billing": return <AdminBilling />;
      case "experts": return <AdminExpertsManager />;
      case "shared": return <SharedDashboard />;
      case "feature-toggles": return <AdminFeatureToggles />;
      case "service-incidents": return <AdminServiceIncidents />;
      default: return null;
    }
  };

  const healthClasses = {
    checking: "bg-amber-50 text-amber-700 border-amber-200",
    online: "bg-emerald-50 text-emerald-700 border-emerald-200",
    degraded: "bg-rose-50 text-rose-700 border-rose-200",
  };

  return (
    <div className="min-h-screen bg-[#f6f6f4] text-gray-900">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-5 py-4 lg:px-8">
          <div className="flex min-w-0 items-center gap-4">
            <button
              type="button"
              onClick={() => handleTabChange("overview")}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-900 font-serif text-xl font-bold text-white"
              aria-label="Retour à la vue d'ensemble"
            >
              A
            </button>
            <div className="min-w-0">
              <h1 className="truncate font-serif text-2xl font-semibold text-gray-950">Administration ARTKIV</h1>
              <p className="truncate text-sm text-gray-500">Observer, prioriser et agir sur la plateforme</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className={`hidden rounded-full border px-3 py-1.5 text-xs font-semibold sm:inline-flex ${healthClasses[health.status]}`}>
              <span className="mr-2">●</span>{health.label}
            </span>
            {stripeHealth && (
              <span
                title={
                  stripeHealth.ok
                    ? "Webhook joignable, secret de signature et clé Stripe configurés."
                    : [
                        !stripeHealth.webhookJwtOff && "webhook bloqué (verify_jwt)",
                        !stripeHealth.webhookSecretConfigured && "STRIPE_WEBHOOK_SECRET absent",
                        !stripeHealth.stripeKeyConfigured && "STRIPE_SECRET_KEY absent",
                      ].filter(Boolean).join(" · ") || "à vérifier"
                }
                className={`hidden rounded-full border px-3 py-1.5 text-xs font-semibold sm:inline-flex ${
                  stripeHealth.ok ? healthClasses.online : healthClasses.degraded
                }`}
              >
                <span className="mr-2">●</span>{stripeHealth.ok ? "Stripe OK" : "Stripe à configurer"}
              </span>
            )}
            <button
              type="button"
              onClick={loadDashboard}
              disabled={loadingMetrics}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60"
            >
              {loadingMetrics ? "Actualisation…" : "Actualiser"}
            </button>
            <Link to="/admin/team-access" className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
              Équipe &amp; accès
            </Link>
            <Link to="/" className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800">
              Retour au site
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-5 py-7 lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Centre de pilotage</p>
            <h2 className="mt-1 text-2xl font-semibold text-gray-950">
              {activeTab === "overview" ? "Vue d’ensemble" : activeItem?.label || "Outil administrateur"}
            </h2>
          </div>
          {lastRefresh && (
            <p className="text-xs text-gray-500">
              Données actualisées à {lastRefresh.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </div>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <MetricCard
            label="À approuver"
            value={loadingMetrics ? "…" : metrics.pendingProfiles}
            helper="profils en attente"
            tone={metrics.pendingProfiles > 0 ? "amber" : "slate"}
            onClick={() => handleTabChange("pending")}
          />
          <MetricCard
            label="Nouveaux comptes"
            value={loadingMetrics ? "…" : metrics.newProfiles7d}
            helper={loadingMetrics ? "7 derniers jours" : `7 j · dont ${metrics.newProfilesCampaign7d} campagne`}
            tone="blue"
          />
          <MetricCard label="Abonnements actifs" value={loadingMetrics ? "…" : metrics.activeSubscriptions} helper="actifs ou en essai" tone="green" onClick={() => handleTabChange("subscriptions")} />
          <MetricCard
            label="Essais expirants"
            value={loadingMetrics ? "…" : metrics.trialsExpiring3d}
            helper={loadingMetrics ? "sous 3 jours" : `sous 3 j · ${metrics.trialsActive} en cours`}
            tone={metrics.trialsExpiring3d > 0 ? "amber" : "slate"}
            onClick={() => { window.location.assign("/admin/campagne"); }}
          />
          <MetricCard
            label="Incidents paiement"
            value={loadingMetrics ? "…" : metrics.paymentIncidents}
            helper="incomplets, impayés ou en retard"
            tone={metrics.paymentIncidents > 0 ? "rose" : "slate"}
            onClick={() => handleTabChange("subscriptions-with-events")}
          />
          <MetricCard label="Œuvres" value={loadingMetrics ? "…" : metrics.artworks.toLocaleString("fr-FR")} helper="en base ARTKIV" tone="slate" />
          <MetricCard label="Profils IA" value={loadingMetrics ? "…" : metrics.aiEnabledProfiles} helper="consentement IA actif" tone="purple" />
        </section>

        {/* Coins CFO & CMO — cockpits par métier */}
        <section className="mt-7 grid gap-5 xl:grid-cols-2">
          {/* Coin CFO — Finance & facturation électronique */}
          <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-sm">
            <div className="h-1.5 bg-emerald-600" />
            <div className="p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Coin CFO</p>
                  <h3 className="mt-1 text-lg font-semibold text-gray-950">Finance &amp; facturation électronique</h3>
                </div>
                <span
                  className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
                  title="Pennylane est la Plateforme Agréée (PDP) qui émet vos Factur-X. Notre connecteur applicatif reste en veille (plan B)."
                >
                  Facturation : Pennylane (PDP)
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <CornerKpi label="Abonnés actifs" value={loadingMetrics ? "…" : metrics.activeSubscriptions} tone="green" />
                <CornerKpi label="Incidents paie." value={loadingMetrics ? "…" : metrics.paymentIncidents} tone={metrics.paymentIncidents > 0 ? "rose" : "slate"} />
                <CornerKpi label="Factures" value={cfo ? cfo.count.toLocaleString("fr-FR") : "—"} />
                <CornerKpi label="Dont B2B" value={cfo ? cfo.b2b : "—"} />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={() => handleTabChange("billing")} className={CORNER_BTN}>Facturation pro</button>
                <button type="button" onClick={() => handleTabChange("subscriptions")} className={CORNER_BTN}>Abonnements</button>
                <button type="button" onClick={() => handleTabChange("subscriptions-with-events")} className={CORNER_BTN}>Réconciliation Stripe</button>
                <button type="button" onClick={() => handleTabChange("subscription-events")} className={CORNER_BTN}>Événements</button>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Réforme e-facture : SIREN/TVA collectés, factures enrichies (identité + ventilation TVA). Les Factur-X sont émis par Pennylane (PDP) ; notre connecteur applicatif reste en veille (plan B).
              </p>
            </div>
          </div>

          {/* Coin CMO — Acquisition & CRM */}
          <div className="overflow-hidden rounded-2xl border border-violet-200 bg-white shadow-sm">
            <div className="h-1.5 bg-violet-600" />
            <div className="p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-700">Coin CMO</p>
                  <h3 className="mt-1 text-lg font-semibold text-gray-950">Acquisition &amp; CRM</h3>
                </div>
                <span className="shrink-0 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                  Campagne Art Advisors
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <CornerKpi label="Leads CRM" value={cmo ? cmo.total : "—"} tone="violet" />
                <CornerKpi label="En essai" value={loadingMetrics ? "…" : metrics.trialsActive} tone="amber" />
                <CornerKpi label="Clients" value={cmo ? cmo.client : "—"} tone="green" />
                <CornerKpi label="Conversion" value={cmo ? `${cmo.conv} %` : "—"} />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/admin/crm" className={`${CORNER_BTN} inline-flex items-center gap-1.5`}><IdCard className="h-3.5 w-3.5" strokeWidth={1.75} /> CRM — Pipeline</Link>
                <Link to="/admin/crm" className={`${CORNER_BTN} inline-flex items-center gap-1.5`}><Mail className="h-3.5 w-3.5" strokeWidth={1.75} /> Campagne email</Link>
                <Link to="/admin/campagne" className={CORNER_BTN}>Entonnoir campagne</Link>
                <Link to="/admin/event-leads" className={CORNER_BTN}>Leads salons</Link>
                <Link to="/admin/grant-free" className={`${CORNER_BTN} inline-flex items-center gap-1.5`}><Gift className="h-3.5 w-3.5" strokeWidth={1.75} /> Offrir un pack</Link>
                <Link to="/transport-demo/preforum" className={CORNER_BTN}>Démo transport</Link>
                <Link to="/insurance-demo/axa" className={CORNER_BTN}>Démo assurance AXA</Link>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Pipeline prospects, séquences email de septembre, packs offerts influenceurs, démos et suivi de conversion.
              </p>
            </div>
          </div>
        </section>

        {activeTab === "overview" && (
          <>
            <section className="mt-7">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-950">Priorités du jour</h3>
                    <p className="mt-1 text-sm text-gray-500">Les éléments qui demandent une intervention humaine.</p>
                  </div>
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">Observer → Agir</span>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  {metrics.serviceIncidents > 0 && (
                    <button type="button" onClick={() => handleTabChange("service-incidents")} className="rounded-xl border border-rose-300 bg-rose-50 p-4 text-left transition hover:bg-rose-100 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900"><AlertTriangle className="h-4 w-4" strokeWidth={1.75} /> Incidents service à traiter</span>
                        <span className="text-xl font-semibold text-rose-700">{metrics.serviceIncidents}</span>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">Erreurs techniques captées côté client (ex. clé OpenAI invalide) et masquées à l’utilisateur. À diagnostiquer côté serveur.</p>
                    </button>
                  )}
                  {metrics.trialsExpiring3d > 0 && (
                    <Link to="/admin/campagne" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-left transition hover:bg-amber-100 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Essais qui expirent sous 3 jours</span>
                        <span className="text-xl font-semibold text-amber-700">{metrics.trialsExpiring3d}</span>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">Fenêtre de relance avant le paywall : ces comptes vont devoir s’abonner. Voir l’entonnoir campagne.</p>
                    </Link>
                  )}
                  <button type="button" onClick={() => handleTabChange("pending")} className="rounded-xl border border-gray-200 p-4 text-left transition hover:border-amber-300 hover:bg-amber-50">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">Profils non approuvés</span>
                      <span className="text-xl font-semibold text-amber-700">{metrics.pendingProfiles}</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">Vérifier l’identité, le rôle et le plan avant ouverture des accès.</p>
                  </button>
                  <button type="button" onClick={() => handleTabChange("subscriptions-with-events")} className="rounded-xl border border-gray-200 p-4 text-left transition hover:border-rose-300 hover:bg-rose-50">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">Paiements à réconcilier</span>
                      <span className="text-xl font-semibold text-rose-700">{metrics.paymentIncidents}</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">Comparer le statut du profil avec les événements Stripe reçus.</p>
                  </button>
                  <button type="button" onClick={() => handleTabChange("validation-requests")} className="rounded-xl border border-gray-200 p-4 text-left transition hover:border-blue-300 hover:bg-blue-50">
                    <div className="font-semibold text-gray-900">Validations métier</div>
                    <p className="mt-2 text-sm text-gray-500">Contrôler les estimations et demandes nécessitant une expertise humaine.</p>
                  </button>
                  <button type="button" onClick={() => handleTabChange("alerts")} className="rounded-xl border border-gray-200 p-4 text-left transition hover:border-violet-300 hover:bg-violet-50">
                    <div className="font-semibold text-gray-900">Alertes IA</div>
                    <p className="mt-2 text-sm text-gray-500">Examiner les signaux produits ou diffusés par les modules intelligents.</p>
                  </button>
                </div>
              </div>

            </section>

            <section className="mt-7 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-5">
                <h3 className="text-lg font-semibold text-gray-950">Import marché Artprice</h3>
                <p className="mt-1 text-sm text-gray-500">Alimenter la base de comparables depuis un rapport PDF.</p>
              </div>
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                <ImportArtprice />
              </div>
            </section>
          </>
        )}

        <section className="mt-7 grid gap-5 lg:grid-cols-2 xl:grid-cols-4">
          {SECTIONS.map((section) => (
            <NavSection key={section.id} section={section} activeTab={activeTab} metrics={metrics} onSelect={handleTabChange} />
          ))}
        </section>

        {activeTab !== "overview" && (
          <section ref={contentRef} className="mt-7 scroll-mt-24 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Outil actif</p>
                <h3 className="mt-1 text-xl font-semibold text-gray-950">{activeItem?.label}</h3>
              </div>
              <button type="button" onClick={() => handleTabChange("overview")} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
                Fermer l’outil
              </button>
            </div>
            <div className="min-h-96 p-5 lg:p-8">{renderActiveTool()}</div>
          </section>
        )}
      </main>
    </div>
  );
}
